import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, unref as _unref, resolveDirective as _resolveDirective, createVNode as _createVNode, withDirectives as _withDirectives, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "row items-center justify-end" }

import { computed } from "vue";
import { AttributeDataType, AttributeInputProps } from "@/types";
import { getIcon } from "@/util";
import TextInput from "./TextInput.vue";
import SelectInput from "./SelectInput.vue";
import ArtifactInput from "./ArtifactInput.vue";
import MultiselectInput from "./MultiselectInput.vue";


/**
 * An input for a generic attribute.
 */
const __default__ = {
  name: "AttributeInput",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    attributes: {},
    attribute: {}
  },
  setup(__props: any) {

const props = __props;

const intRules = computed(
  () =>
    (value: string | number | undefined): string | true => {
      if (value === undefined || value === "") {
        return true;
      } else {
        const strValue = typeof value === "string" ? value : String(value);

        return strValue.includes(".") ? "Must be a valid integer." : true;
      }
    }
);

const numRules = computed(
  () =>
    (value: string | number | undefined): string | true => {
      const { min = null, max = null } = props.attribute;
      const numValue = typeof value === "string" ? parseFloat(value) : value;

      if (numValue === undefined || isNaN(numValue)) {
        return true;
      } else if (max !== null && numValue > max) {
        return `Value is greater than ${max}.`;
      } else if (min !== null && numValue < min) {
        return `Value is less than ${min}.`;
      } else {
        return true;
      }
    }
);

const lengthRules = computed(
  () =>
    (value: string | string[] | undefined): string | true => {
      const { min = null, max = null } = props.attribute;
      const unit = Array.isArray(value) ? "items" : "characters";

      if (!value) {
        return true;
      } else if (max !== null && value.length > max) {
        return `Value has greater than ${max} ${unit}.`;
      } else if (min !== null && value.length < min) {
        return `Value has less than ${min} ${unit}.`;
      } else {
        return true;
      }
    }
);

/**
 * Handles input changes to make adjustments to the stored data types.
 *
 * @param value - The updated value of this attribute.
 */
function handleInput(value: AttributeDataType): void {
  if (props.attribute.type === "int") {
    props.attributes[props.attribute.key] = parseInt(String(value));
  } else if (props.attribute.type === "float") {
    props.attributes[props.attribute.key] = parseFloat(String(value));
  } else if (props.attribute.type === "date") {
    props.attributes[props.attribute.key] = new Date(
      String(value)
    ).toISOString();
  } else {
    props.attributes[props.attribute.key] = value;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_q_checkbox = _resolveComponent("q-checkbox")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_date = _resolveComponent("q-date")!
  const _component_q_popup_proxy = _resolveComponent("q-popup-proxy")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_input = _resolveComponent("q-input")!
  const _directive_close_popup = _resolveDirective("close-popup")!

  return (_ctx.attribute.type === 'text')
    ? (_openBlock(), _createBlock(TextInput, {
        key: 0,
        label: _ctx.attribute.label,
        rules: [lengthRules.value],
        "model-value": _ctx.attributes[_ctx.attribute.key] as string,
        "onUpdate:modelValue": handleInput
      }, null, 8, ["label", "rules", "model-value"]))
    : (_ctx.attribute.type === 'paragraph')
      ? (_openBlock(), _createBlock(TextInput, {
          key: 1,
          type: "textarea",
          label: _ctx.attribute.label,
          rules: [lengthRules.value],
          "model-value": _ctx.attributes[_ctx.attribute.key] as string,
          "onUpdate:modelValue": handleInput
        }, null, 8, ["label", "rules", "model-value"]))
      : (_ctx.attribute.type === 'select')
        ? (_openBlock(), _createBlock(SelectInput, {
            key: 2,
            label: _ctx.attribute.label,
            options: _ctx.attribute.options || [],
            "model-value": _ctx.attributes[_ctx.attribute.key] as string,
            "onUpdate:modelValue": handleInput
          }, null, 8, ["label", "options", "model-value"]))
        : (_ctx.attribute.type === 'multiselect')
          ? (_openBlock(), _createBlock(MultiselectInput, {
              key: 3,
              label: _ctx.attribute.label,
              options: _ctx.attribute.options || [],
              rules: [lengthRules.value],
              "model-value": _ctx.attributes[_ctx.attribute.key] as string[],
              "onUpdate:modelValue": handleInput as (value: string[]) => void
            }, null, 8, ["label", "options", "rules", "model-value", "onUpdate:modelValue"]))
          : (_ctx.attribute.type === 'relation')
            ? (_openBlock(), _createBlock(ArtifactInput, {
                key: 4,
                multiple: "",
                label: _ctx.attribute.label,
                rules: [lengthRules.value],
                "model-value": _ctx.attributes[_ctx.attribute.key] as string[],
                "onUpdate:modelValue": handleInput
              }, null, 8, ["label", "rules", "model-value"]))
            : (_ctx.attribute.type === 'int')
              ? (_openBlock(), _createBlock(TextInput, {
                  key: 5,
                  type: "number",
                  label: _ctx.attribute.label,
                  rules: [intRules.value, numRules.value],
                  "model-value": _ctx.attributes[_ctx.attribute.key] as number,
                  "onUpdate:modelValue": handleInput
                }, null, 8, ["label", "rules", "model-value"]))
              : (_ctx.attribute.type === 'float')
                ? (_openBlock(), _createBlock(TextInput, {
                    key: 6,
                    type: "number",
                    label: _ctx.attribute.label,
                    rules: [numRules.value],
                    "model-value": _ctx.attributes[_ctx.attribute.key] as number,
                    "onUpdate:modelValue": handleInput
                  }, null, 8, ["label", "rules", "model-value"]))
                : (_ctx.attribute.type === 'boolean')
                  ? (_openBlock(), _createBlock(_component_q_checkbox, {
                      key: 7,
                      label: _ctx.attribute.label,
                      "model-value": _ctx.attributes[_ctx.attribute.key],
                      class: "q-my-sm",
                      "onUpdate:modelValue": handleInput
                    }, null, 8, ["label", "model-value"]))
                  : (_ctx.attribute.type === 'date')
                    ? (_openBlock(), _createBlock(_component_q_input, {
                        key: 8,
                        filled: "",
                        label: _ctx.attribute.label,
                        "model-value": _ctx.attributes[_ctx.attribute.key] as string,
                        "onUpdate:modelValue": handleInput
                      }, {
                        append: _withCtx(() => [
                          _createVNode(_component_q_icon, {
                            name: _unref(getIcon)('calendar'),
                            class: "cursor-pointer"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_popup_proxy, {
                                cover: "",
                                "transition-show": "scale",
                                "transition-hide": "scale"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_q_date, {
                                    "model-value": _ctx.attributes[_ctx.attribute.key],
                                    mask: "YYYY-MM-DDTHH:mm:00.000Z",
                                    "onUpdate:modelValue": handleInput
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_1, [
                                        _withDirectives(_createVNode(_component_q_btn, {
                                          label: "Close",
                                          color: "primary",
                                          flat: ""
                                        }, null, 512), [
                                          [_directive_close_popup]
                                        ])
                                      ])
                                    ]),
                                    _: 1
                                  }, 8, ["model-value"])
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }, 8, ["name"])
                        ]),
                        _: 1
                      }, 8, ["label", "model-value"]))
                    : _createCommentVNode("", true)
}
}

})